import { BrowserRouter as Router, Route, Routes, Link, useNavigate, Navigate } from 'react-router-dom';
import { Component, useState, useEffect } from 'react';
import axios from 'axios';
import Home from './pages/Home';
import SystemTopics from './pages/SystemTopics';
import MyTopics from './pages/MyTopics';
import Profile from './pages/Profile';
import Verify from './pages/Verify';
import SignOn from './pages/SignOn';
import TopicDetails from './pages/TopicDetails';
import AdminReport from './pages/AdminReport'; // Added import for new page
import CollegeFantasy from './pages/CollegeFantasy';
import './App.css';
import Maintenance from './pages/Maintenance';
import { applyAppearance, loadStoredAppearance } from './theme';
import { getTeamById } from './data/teams';

// Error boundary to catch rendering errors
class ErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong. Please try again.</h1>;
    }
    return this.props.children;
  }
}

// ProtectedRoute component to restrict access
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const isSignedIn = !!localStorage.getItem('token'); // Check token presence
  const isAdmin = localStorage.getItem('isAdmin') === 'true'; // Check admin status
  if (!isSignedIn) {
    return <Navigate to="/sign-on" replace />;
  }
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const FALLBACK_NOTIFICATIONS = [
  {
    text: 'The 2026 College Football is Here!  You are invited to Vote on the 2026 Official MYTOP25 College Football Preseason Poll.',
    url: '/system-topics',
  },
  {
    text: 'SHARE IDEAS AND FEEDBACK ON X',
    url: 'https://x.com/TheMyTop25',
  },
];

const NotificationLink = ({ item }) => {
  const url = (item.url || '').trim();
  if (!url) return item.text;
  if (url.startsWith('/')) {
    return <Link to={url}>{item.text}</Link>;
  }
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  return <a href={href} target="_blank" rel="noopener noreferrer">{item.text}</a>;
};

function App() {
  const stored = loadStoredAppearance();
  const [username, setUsername] = useState('');
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem('isAdmin') === 'true');
  const [theme, setTheme] = useState(stored.theme);
  const [spearheadTeamId, setSpearheadTeamId] = useState((stored.team && stored.team.id) || '');
  const [notifications, setNotifications] = useState(FALLBACK_NOTIFICATIONS);
  const [maintenanceOn, setMaintenanceOn] = useState(false);
  const navigate = useNavigate();

  // Construct API URL
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';

  useEffect(() => {
    const loadNotifications = () => {
      axios.get(`${apiUrl}/api/notifications`)
        .then((response) => {
          if (response.data && Array.isArray(response.data.items)) {
            setNotifications(response.data.items);
          }
        })
        .catch(() => {});
    };

    loadNotifications();
    window.addEventListener('notifications-updated', loadNotifications);
    return () => window.removeEventListener('notifications-updated', loadNotifications);
  }, [apiUrl]);

  useEffect(() => {
    const loadSettings = () => {
      axios.get(`${apiUrl}/api/settings`)
        .then((response) => {
          setMaintenanceOn(Boolean(response.data && response.data.maintenance));
        })
        .catch(() => {});
    };

    loadSettings();
    const onMaintenance = (event) => {
      if (event.detail && typeof event.detail.maintenance === 'boolean') {
        setMaintenanceOn(event.detail.maintenance);
      } else {
        loadSettings();
      }
    };
    window.addEventListener('maintenance-updated', onMaintenance);
    return () => window.removeEventListener('maintenance-updated', onMaintenance);
  }, [apiUrl]);

  useEffect(() => {
    const current = loadStoredAppearance();
    applyAppearance({ theme, team: getTeamById(spearheadTeamId) || current.team });
  }, [theme, spearheadTeamId]);

  useEffect(() => {
    const onAppearance = (event) => {
      const detail = event.detail || {};
      if (detail.theme) setTheme(detail.theme);
      if (Object.prototype.hasOwnProperty.call(detail, 'spearheadTeamId')) {
        setSpearheadTeamId(detail.spearheadTeamId || '');
      }
    };
    window.addEventListener('appearance-updated', onAppearance);
    return () => window.removeEventListener('appearance-updated', onAppearance);
  }, []);

  useEffect(() => {
    const onSignOff = () => {
      setIsSignedIn(false);
      setIsAdmin(false);
      setUsername('');
      setSpearheadTeamId('');
    };
    window.addEventListener('auth-signed-off', onSignOff);
    return () => window.removeEventListener('auth-signed-off', onSignOff);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsSignedIn(false);
      setIsAdmin(false);
    } else {
      axios.get(`${apiUrl}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(response => {
          const user = response.data.user || {};
          setUsername(user.name);
          setIsSignedIn(true);
          const admin = user.role === 'admin';
          setIsAdmin(admin);
          localStorage.setItem('isAdmin', admin);
          if (user.theme === 'light' || user.theme === 'dark') {
            setTheme(user.theme);
          }
          const nextSpearhead = user.spearheadTeamId
            || (Array.isArray(user.favoriteTeams) && user.favoriteTeams.length === 1 ? user.favoriteTeams[0].id : '');
          setSpearheadTeamId(nextSpearhead || '');
        })
        .catch(error => {
          const status = error.response && error.response.status;
          console.error('Failed to verify token:', error);
          if (status === 429) {
            return;
          }
          localStorage.removeItem('token');
          localStorage.removeItem('isAdmin');
          setIsSignedIn(false);
          setIsAdmin(false);
          setSpearheadTeamId('');
          navigate('/sign-on');
        });
    }
  }, [navigate]);

  const envMaintenance = process.env.REACT_APP_MAINTENANCE_MODE === 'true';
  if ((maintenanceOn || envMaintenance) && !isAdmin) {
    return <Maintenance />;
  }

  return (
    <div className="app">
      <nav className="left-nav">
        <img src="/logo.png" alt="MyTop25 Logo" className="nav-logo" />
        <ul className="nav-links">
          <li><Link to="/">Home</Link></li>
          <li><Link to="/system-topics">Official Polls</Link></li>
          <li><Link to="/college-fantasy">College Fantasy</Link></li>
          {isSignedIn && (
            <li><Link to="/my-topics">My Polls</Link></li>
          )}
          {isAdmin && (
            <li><Link to="/admin-report">Admin Report</Link></li>
          )}
        </ul>
        <div className="notification-area">
          <h3>Notifications</h3>
          <ul>
            {notifications.length ? (
              notifications.map((item, index) => (
                <li key={`${item.text}-${index}`}>
                  <NotificationLink item={item} />
                </li>
              ))
            ) : (
              <li>No notifications right now.</li>
            )}
          </ul>
        </div>
        {isSignedIn ? (
          <Link to="/profile" className="nav-user-card">
            <span className="nav-user-avatar" aria-hidden="true">
              {(username || '?').charAt(0).toUpperCase()}
            </span>
            <span className="nav-user-copy">
              <span className="nav-user-name">{username}</span>
              <span className="nav-user-label">My Profile</span>
            </span>
          </Link>
        ) : (
          <Link to="/sign-on" className="nav-user-card nav-user-guest">
            <span className="nav-user-avatar" aria-hidden="true">?</span>
            <span className="nav-user-copy">
              <span className="nav-user-name">Guest</span>
              <span className="nav-user-label">Sign On</span>
            </span>
          </Link>
        )}
      </nav>
      <div className="content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/sign-on" element={<SignOn />} />
          <Route path="/system-topics" element={<SystemTopics />} />
          <Route path="/college-fantasy" element={<CollegeFantasy />} />
          <Route path="/topic/:id" element={<TopicDetails />} />
          <Route path="/verify" element={<Verify />} />
          <Route
            path="/my-topics"
            element={
              <ProtectedRoute>
                <MyTopics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-report"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminReport />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<h1>404: Page Not Found. Oops, that page does not exist!</h1>} />
        </Routes>
      </div>
    </div>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </Router>
  );
}
